"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

import { useSession } from "@/app/hooks/useSession";
import { syncApi } from "@/lib/sync/client";
import type { Operation, SyncState } from "@/lib/types";

import { useOnlineStatus } from "./useOnlineStatus";

export function useSync() {
  const { isAuthenticated } = useSession();
  const { isOffline } = useOnlineStatus();
  const queryClient = useQueryClient();
  const [syncState, setSyncState] = useState<SyncState>({
    status: "idle",
    lastSynced: null,
    error: null,
  });
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);

  // Fetch sync data (only when authenticated) - initial load only
  const {
    data: syncData,
    refetch,
    isError,
    error,
    isSuccess,
    isFetching,
  } = useQuery({
    queryKey: ["sync"],
    queryFn: syncApi.fetchSync,
    enabled: isAuthenticated && !isOffline,
    staleTime: Infinity, // Never refetch automatically - user must manually sync
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnReconnect: false, // Don't refetch when reconnecting to network
  });

  // Update sync state when query state changes
  useEffect(() => {
    // Set status to "syncing" when the query starts fetching
    // The RefreshCw icon will spin (because isSyncing becomes true)
    // When the fetch completes, status changes to "success"
    // The SyncStatus badge animation triggers properly
    if (isFetching) {
      setSyncState((prev) => ({
        ...prev,
        status: "syncing",
        error: null,
      }));
    } else if (isSuccess && syncData) {
      setSyncState({
        status: "success",
        lastSynced: syncData.lastSynced || new Date().toISOString(),
        error: null,
      });
      setLastSyncedAt(syncData.lastSynced || new Date().toISOString());
    } else if (isError) {
      setSyncState({
        status: "error",
        lastSynced: syncState.lastSynced,
        error: (error as any)?.message || "Failed to sync data",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching, isSuccess, isError, syncData, error]);

  // Sync operations mutation
  const syncMutation = useMutation({
    mutationFn: (operations: Operation[]) => syncApi.syncOperations(operations, lastSyncedAt),
    onMutate: () => {
      console.log("🔄 Sync mutation started (onMutate)");
      setSyncState((prev) => ({
        ...prev,
        status: "syncing",
        error: null,
      }));
    },
    onSuccess: (data: any) => {
      console.log("✅ Sync mutation succeeded (onSuccess)");
      setSyncState({
        status: "success",
        lastSynced: data.syncedAt || new Date().toISOString(),
        error: null,
      });
      setLastSyncedAt(data.syncedAt || new Date().toISOString());
      queryClient.invalidateQueries({ queryKey: ["sync"] });
    },
    onError: (error: any) => {
      console.log("❌ Sync mutation failed (onError)");
      setSyncState((prev) => ({
        status: "error",
        lastSynced: prev.lastSynced,
        error: error?.message || "Failed to sync operations",
      }));
    },
    onSettled: () => {
      console.log("🏁 Sync mutation settled (onSettled)");
    },
  });

  // Manual sync function for user-triggered syncs
  const manualSync = useCallback(async () => {
    if (!isAuthenticated) return;

    setSyncState((prev) => ({
      ...prev,
      status: "syncing",
      error: null,
    }));

    try {
      await refetch();
    } catch (error) {
      console.error("Manual sync failed:", error);
    }
  }, [isAuthenticated, refetch]);

  // Sync operations to server with retry logic
  const syncOperations = useCallback(
    async function attemptSync(operations: Operation[], retryCount = 0): Promise<Operation[]> {
      if (!isAuthenticated || operations.length === 0) return [];

      const MAX_RETRIES = 2; // Reduced from 3 to prevent loops
      const RETRY_DELAYS = [1000, 3000]; // 1s, 3s (removed 4s third retry)

      console.log(
        `🔄 syncOperations called with ${operations.length} operation(s), retry: ${retryCount}`
      );

      try {
        const result = await syncMutation.mutateAsync(operations);
        console.log(`✅ syncOperations completed successfully`);
        // Ensure we always return an array, even if serverChanges is undefined
        return result.serverChanges || [];
      } catch (error: any) {
        // Don't retry on limit errors or client errors (4xx)
        const isLimitError = error?.response?.data?.error === "limitExceeded";
        const isClientError = error?.response?.status >= 400 && error?.response?.status < 500;

        if (isLimitError || isClientError) {
          console.error("❌ Sync failed with client error (no retry):", error);
          throw error;
        }

        // Retry on network/server errors
        if (retryCount < MAX_RETRIES) {
          const delay = RETRY_DELAYS[retryCount];
          console.log(
            `⏳ Sync failed, retrying in ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRIES})...`
          );

          // Update sync state to show retrying
          setSyncState((prev) => ({
            ...prev,
            status: "syncing",
            error: `Retrying... (${retryCount + 1}/${MAX_RETRIES})`,
          }));

          // Wait before retrying
          await new Promise((resolve) => setTimeout(resolve, delay));

          // Retry
          return attemptSync(operations, retryCount + 1);
        } else {
          // Max retries exceeded
          console.error("❌ Sync failed after max retries:", error);
          throw error;
        }
      }
    },
    [isAuthenticated, syncMutation]
  );

  // Calculate isSyncing
  const isSyncing = syncState.status === "syncing" || syncMutation.isPending;

  // Log isSyncing state changes (for debugging)
  useEffect(() => {
    console.log(
      `🔄 isSyncing: ${isSyncing} (status: ${syncState.status}, isPending: ${syncMutation.isPending})`
    );
  }, [isSyncing, syncState.status, syncMutation.isPending]);

  return {
    syncData: syncData?.state,
    preferences: syncData?.preferences,
    syncState,
    syncOperations,
    manualSync,
    // CRITICAL: isSyncing should be true whenever:
    // 1. syncState.status is "syncing" (manually set)
    // 2. syncMutation.isPending is true (mutation in flight)
    isSyncing,
  };
}
